import { Component } from '@theme/component';

/**
 * Tabs for wld-product-details (rules/reference/accessibility-patterns.md § 8). The server renders
 * every panel, the first one visible; this switches `aria-selected`, the roving `tabindex` and the
 * panels' `hidden`. Arrow keys move focus and activate (Horizon convention), Home / End jump. In the
 * theme editor, selecting a tab block shows its panel.
 *
 * @typedef {{ tabs: HTMLButtonElement[], panels: HTMLElement[] }} Refs
 * @extends {Component<Refs>}
 */
class WldProductDetails extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /**
   * @param {Event} event
   */
  select(event) {
    if (event.target instanceof HTMLButtonElement) this.#activate(event.target);
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    this.#activate(tab);
    tab.focus();
  }

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const panel = event.target.closest('[role="tabpanel"]');
    const tab = (this.refs.tabs ?? []).find((t) => t.getAttribute('aria-controls') === panel?.id);
    if (tab) this.#activate(tab);
  };

  /**
   * @param {HTMLButtonElement} activeTab
   */
  #activate(activeTab) {
    for (const tab of this.refs.tabs ?? []) {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    const panelId = activeTab.getAttribute('aria-controls');
    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.id !== panelId;
    }
  }
}

if (!customElements.get('wld-product-details')) {
  customElements.define('wld-product-details', WldProductDetails);
}
